import { useQuery } from '@tanstack/react-query'
import { ArrowRight, ExternalLink, Lock, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'wouter'
import { api } from '../lib/api'
import { fmt, pct } from '../lib/format'
import { useVersions } from '../lib/hooks'
import type { KpiDetail, TreeExhibit } from '../lib/types'
import { Chart } from './Chart'
import { CompactTree } from './DriverTree'
import { ExhibitCard, ExhibitViewer, linkFor } from './Exhibits'
import { useAgent, useWorkspace } from './Shell'
import { Drawer, ErrorBox, Loading, Spinner } from './ui'
import { VersionSelect } from './Versions'

type Good = 'up' | 'down' | null

/** Favorable is green and unfavorable red when the KPI's direction is known; neutral otherwise. */
export function tone(delta: number | null, good: Good): string | undefined {
  if (delta === null || Math.abs(delta) < 0.5 || good === null) return undefined
  return (good === 'up' ? delta > 0 : delta < 0) ? 'var(--green)' : 'var(--red)'
}

/** A card's trend: the version as a line, its comparison dashed, a point-in-time KPI marked. */
export function Sparkline({
  values,
  compare,
  at,
  closed = 0,
}: {
  values: (number | null)[]
  compare?: (number | null)[] | null
  at?: number | null
  closed?: number
}) {
  const all = [...values, ...(compare ?? [])].filter((v): v is number => v !== null)
  if (values.length < 2 || !all.length) return <div className="spark empty" />
  const W = 200
  const H = 34
  const lo = Math.min(...all)
  const hi = Math.max(...all)
  const x = (i: number) => (i * W) / (values.length - 1)
  const y = (v: number) => H - 3 - ((v - lo) / (hi - lo || 1)) * (H - 6)
  const path = (vs: (number | null)[]) => {
    let d = ''
    let pen = false
    vs.forEach((v, i) => {
      if (v === null) {
        pen = false
        return
      }
      d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`
      pen = true
    })
    return d
  }
  const atV = at !== null && at !== undefined && at >= 0 ? values[at] : null
  return (
    <svg className="spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden>
      {closed > 0 && (
        <>
          <rect x={0} y={0} width={Math.min(W, x(closed - 1) + W / (values.length - 1) / 2)} height={H} fill="var(--actual-bg)" />
          {closed < values.length && (
            <line
              x1={x(closed - 1) + W / (values.length - 1) / 2}
              x2={x(closed - 1) + W / (values.length - 1) / 2}
              y1={0}
              y2={H}
              stroke="var(--border-strong)"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </>
      )}
      {compare && (
        <path d={path(compare)} fill="none" stroke="var(--faint)" strokeWidth={1} strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
      )}
      <path d={path(values)} fill="none" stroke="var(--data)" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
      {atV !== null && atV !== undefined && at !== null && at !== undefined && <circle cx={x(at)} cy={y(atV)} r={2.5} fill="var(--data)" />}
    </svg>
  )
}

export type KpiCardData = {
  key: string
  label: string
  format: string
  value: number
  version: { key: string; label: string }
  compare: { key: string; label: string; value: number | null } | null
  good: Good
  spark: (number | null)[]
  sparkCompare: (number | null)[] | null
  sparkAt: number | null
  sparkClosed?: number
}

/** A Home KPI: the number, its variance, its trend; click for everything behind it. */
export function KpiCard({ k, onOpen }: { k: KpiCardData; onOpen: () => void }) {
  const base = k.compare?.value ?? null
  const d = base !== null ? k.value - base : null
  const p = d !== null && base ? d / Math.abs(base) : null
  return (
    <button type="button" className="kpi-card" onClick={onOpen} aria-label={`${k.label}: open the variance and drivers`}>
      <div className="stat-label">{k.label}</div>
      <div className="stat-value">{fmt(k.value, k.format)}</div>
      <div className="kpi-delta">
        {d !== null && k.compare ? (
          Math.abs(d) < 0.5 ? (
            <span className="faint">in line with {k.compare.label}</span>
          ) : (
            <>
              <span style={{ color: tone(d, k.good) }} className="num">
                {fmt(d, k.format, true)}
                {p !== null && Math.abs(p) >= 0.0005 ? ` (${pct(p, 1, true)})` : ''}
              </span>{' '}
              <span className="faint">vs {k.compare.label}</span>
            </>
          )
        ) : (
          <span className="faint">{k.version.label}</span>
        )}
      </div>
      <Sparkline values={k.spark} compare={k.sparkCompare} at={k.sparkAt} closed={k.sparkClosed} />
      <ArrowRight size={13} className="kpi-go" />
    </button>
  )
}

// ---------------------------------------------------------------- bridge

/**
 * The variance as a bridge: the comparison, each owner's contribution (largest first), the
 * version. The axis starts near the data (not at zero) so a small change on a large number
 * stays visible; the start and end bars are drawn broken to say so.
 */
export function Bridge({ d }: { d: KpiDetail }) {
  const b = d.bridge
  if (!b) return null
  const f = d.kpi.format
  let cum = b.start
  const steps = b.steps.map(s => {
    const from = cum
    cum += s.delta
    return { ...s, from, to: cum }
  })
  const points = [b.start, b.end, ...steps.flatMap(s => [s.from, s.to])]
  const lo0 = Math.min(...points)
  const hi0 = Math.max(...points)
  const pad = (hi0 - lo0) * 0.12 || Math.abs(hi0) * 0.01 || 1
  const lo = lo0 - pad
  const hi = hi0 + pad * 0.4
  const pos = (v: number) => ((v - lo) / (hi - lo)) * 100
  const broken = lo > 0
  const Row = ({ label, from, to, kind, value }: { label: string; from: number; to: number; kind: 'total' | 'step'; value: number }) => {
    const l = pos(Math.min(from, to))
    const w = Math.max(0.6, Math.abs(pos(to) - pos(from)))
    const color = kind === 'total' ? 'var(--text)' : (tone(value, d.kpi.good) ?? (value >= 0 ? 'var(--data)' : 'var(--amber)'))
    return (
      <div className={`bridge-row ${kind}`}>
        <span className="bridge-label" title={label}>
          {label}
        </span>
        <span className="bridge-track">
          <span
            className={`bridge-bar ${kind === 'total' && broken ? 'broken' : ''}`}
            style={{ left: `${l}%`, width: `${w}%`, background: color }}
          />
        </span>
        <span className="bridge-value num" style={kind === 'step' ? { color } : undefined}>
          {kind === 'total' ? fmt(value, f) : fmt(value, f, true)}
        </span>
      </div>
    )
  }
  return (
    <div className="bridge" role="img" aria-label={`Bridge from ${d.compare?.label} to ${d.version.label} by ${b.levelLabel}`}>
      <Row label={d.compare?.label ?? 'Compare'} from={lo} to={b.start} kind="total" value={b.start} />
      {steps.map(s => (
        <Row key={s.id} label={s.label} from={s.from} to={s.to} kind="step" value={s.delta} />
      ))}
      <Row label={d.version.label} from={lo} to={b.end} kind="total" value={b.end} />
      {broken && <div className="bridge-note">Axis starts at {fmt(lo, f)} so the changes are visible</div>}
    </div>
  )
}

// ---------------------------------------------------------------- drill-down

/** Everything behind a KPI card: variance, the so-what, bridge, trend, variance by owner, and drivers. */
export function KpiDrawer({ kpiKey, label, onClose }: { kpiKey: string; label: string; onClose: () => void }) {
  const { wid, state } = useWorkspace()
  const { ask } = useAgent()
  const versions = useVersions(wid)
  const [compare, setCompare] = useState<string | null | undefined>(undefined)
  const [why, setWhy] = useState<{ level: string; key: string; label: string } | null>(null)
  const [diagram, setDiagram] = useState<TreeExhibit | null>(null)
  const q = useQuery({
    // undefined = the KPI's default comparison; null = none
    queryKey: ['ws', wid, 'kpi', kpiKey, compare === undefined ? '__default' : (compare ?? '__none')],
    queryFn: () =>
      api.get<KpiDetail>(`/w/${wid}/kpis/${kpiKey}/detail${compare === undefined ? '' : `?compare=${encodeURIComponent(compare ?? '')}`}`),
    placeholderData: prev => prev,
  })
  const d = q.data
  const coord = d
    ? (Object.fromEntries(
        Object.entries(d.filters)
          .filter(([, ids]) => ids.length === 1)
          .map(([l, ids]) => [l, ids[0] as string]),
      ) as Record<string, string>)
    : {}
  const singleSlice = d ? Object.values(d.filters).every(ids => ids.length === 1) : false
  const whyQ = useQuery({
    queryKey: ['ws', wid, 'kpi-why', kpiKey, d?.compare?.key ?? null, why?.level, why?.key],
    enabled: !!d && !!why && singleSlice,
    queryFn: () => {
      const p = new URLSearchParams({
        measure: d?.kpi.measure as string,
        coord: JSON.stringify({ ...coord, [why?.level as string]: why?.key }),
        version: d?.version.key as string,
        depth: '4',
      })
      if (d?.compare) p.set('compare', d.compare.key)
      return api.get<TreeExhibit>(`/w/${wid}/explain/tree?${p}`)
    },
  })
  const noSnapshots = !(versions.data?.versions ?? []).some(v => v.kind === 'snapshot')
  const canFreeze = state?.role === 'owner' || state?.role === 'admin' || state?.role === 'planner'
  const d0 = d?.delta ?? null

  return (
    <Drawer
      wide
      title={d?.kpi.label ?? label}
      sub={d ? `${d.version.label}${d.compare ? ` vs ${d.compare.label}` : ''}` : 'Loading…'}
      onClose={onClose}
      actions={
        versions.data && d ? (
          <VersionSelect
            label="Compare"
            value={d.compare?.key ?? null}
            versions={versions.data.versions.filter(v => v.key !== d.version.key)}
            cycles={versions.data.cycles}
            allowNone
            onChange={v => {
              setWhy(null)
              setCompare(v)
            }}
          />
        ) : null
      }
    >
      {q.error ? (
        <ErrorBox error={q.error} />
      ) : !d ? (
        <Loading rows={8} />
      ) : (
        <div className={`kpi-detail ${q.isFetching ? 'refreshing' : ''}`}>
          <div className="kpi-hero">
            <div>
              <div className="kpi-hero-value">{fmt(d.value, d.kpi.format)}</div>
              <div className="small muted">{d.version.label}</div>
            </div>
            {d.compare && d.base !== null && (
              <div className="kpi-hero-delta">
                <div className="kpi-hero-change num" style={{ color: tone(d0, d.kpi.good) }}>
                  {d0 !== null && Math.abs(d0) >= 0.5 ? fmt(d0, d.kpi.format, true) : 'no change'}
                  {d.deltaPct !== null && Math.abs(d.deltaPct) >= 0.0005 && (
                    <span className="kpi-hero-pct"> {pct(d.deltaPct, 1, true)}</span>
                  )}
                </div>
                <div className="small muted">
                  vs {d.compare.label} ({fmt(d.base, d.kpi.format)})
                  {d.favorable !== null && (
                    <span className={`pill ${d.favorable ? 'green' : 'red'}`} style={{ marginLeft: 8 }}>
                      {d.favorable ? 'Favorable' : 'Unfavorable'}
                    </span>
                  )}
                </div>
              </div>
            )}
            {q.isFetching && <Spinner size={12} />}
          </div>

          <div className="kpi-sowhat">
            <div className="eyebrow">So what</div>
            <p>{d.summary}</p>
            {d.notes.map(n => (
              <p key={n} className="small muted" style={{ fontSize: 12.5 }}>
                {n}
              </p>
            ))}
          </div>

          {!d.compare && (
            <div className="notice row small" style={{ alignItems: 'flex-start' }}>
              <Lock size={14} style={{ marginTop: 2 }} />
              <span className="grow">
                {noSnapshots
                  ? 'There is nothing to compare against yet. Freeze the working forecast as a budget to track variances here, on every KPI and in every view.'
                  : 'Choose a version to compare against (top right) to see the variance and what explains it.'}
              </span>
              {noSnapshots && canFreeze && (
                <Link href="/plans" className="btn sm" onClick={onClose}>
                  Freeze a budget
                </Link>
              )}
            </div>
          )}

          <div className={`kpi-grid2 ${d.bridge ? '' : 'single'}`}>
            {d.bridge && (
              <section className="kpi-section">
                <h3>
                  Bridge by {d.bridge.levelLabel.toLowerCase()}
                  <span className="faint small">
                    {' '}
                    · {d.compare?.label} → {d.version.label}
                  </span>
                </h3>
                <Bridge d={d} />
              </section>
            )}
            <section className="kpi-section">
              <h3>Trend</h3>
              <Chart
                x={d.trend.x}
                series={d.trend.series}
                format={d.trend.measure.format}
                mode="line"
                height={d.bridge ? 220 : 240}
                compact
              />
            </section>
          </div>

          <div className="kpi-cards">
            {d.breakdowns.map(t => (
              <ExhibitCard
                key={t.title}
                e={{
                  ...t,
                  title: d.compare ? `Variance by ${t.rowLevels[0]?.label.toLowerCase()}` : `By ${t.rowLevels[0]?.label.toLowerCase()}`,
                  caption: singleSlice ? 'Click a row to see what drives it' : null,
                }}
                good={d.kpi.good}
                activeRow={why && why.level === t.rowLevels[0]?.name ? why.key : null}
                onRow={
                  singleSlice
                    ? (key, lbl) => setWhy({ level: t.rowLevels[0]?.name as string, key: key[0] as string, label: lbl })
                    : undefined
                }
              />
            ))}
          </div>

          {why && (
            <section className="kpi-section kpi-why">
              <h3>
                Why: {why.label}
                <span className="faint small">
                  {' '}
                  · {d.version.label}
                  {d.compare ? ` vs ${d.compare.label}` : ''}
                </span>
                <div className="grow" />
                {whyQ.data && (
                  <button
                    type="button"
                    className="link-btn strong"
                    onClick={() => setDiagram({ ...whyQ.data, title: `Why: ${why.label}`, caption: d.kpi.label })}
                  >
                    Diagram
                  </button>
                )}
                <button type="button" className="link-btn" onClick={() => setWhy(null)}>
                  Close
                </button>
              </h3>
              {whyQ.isLoading ? (
                <Loading rows={4} />
              ) : whyQ.error ? (
                <ErrorBox error={whyQ.error} />
              ) : whyQ.data ? (
                <CompactTree root={whyQ.data.root} maxDepth={2} good={d.kpi.good} />
              ) : null}
            </section>
          )}

          {d.tree && !why && (
            <ExhibitCard
              e={{ ...d.tree, title: `How ${d.kpi.label} is built`, caption: 'Each driver with its change from the comparison' }}
              good={d.kpi.good}
            />
          )}

          <div className="kpi-actions">
            <Link href={linkFor(d.breakdowns[0]?.view ?? d.trend.view)} className="btn sm" onClick={onClose}>
              <ExternalLink size={12} /> Open in view
            </Link>
            <button
              type="button"
              className="btn sm primary"
              onClick={() => {
                onClose()
                ask(
                  d.compare
                    ? `${d.kpi.label} is ${fmt(d0 ?? 0, d.kpi.format, true)} vs ${d.compare.label}. What drives the variance, which of it is timing versus a real change, and what should we do about it? Show the evidence.`
                    : `Walk me through ${d.kpi.label} (${fmt(d.value, d.kpi.format)} in the ${d.version.label}): what drives it and what stands out?`,
                  `KPI ${d.kpi.key} (${d.kpi.measure} ${JSON.stringify(d.filters)})`,
                )
              }}
            >
              <Sparkles size={12} /> Ask the agent
            </button>
          </div>
        </div>
      )}
      {diagram && <ExhibitViewer e={diagram} onClose={() => setDiagram(null)} />}
    </Drawer>
  )
}
